import Cookies from 'js-cookie'

import {
  buildHostCookieAttributes,
  buildSharedCookieAttributes,
  type CookieLifetimeOptions,
} from './cookiePolicy.js'

/**
 * Writes sensitive application state as a host-only cookie, then removes any
 * legacy domain-scoped copy. The final read and re-apply protects the target
 * value when duplicate-cookie cleanup also removes the first host-only write.
 */
export function setHostCookie(
  key: string,
  value: string,
  options: CookieLifetimeOptions = {},
): void {
  const hostAttributes = buildHostCookieAttributes(options)
  Cookies.set(key, value, hostAttributes)

  const sharedAttributes = buildSharedCookieAttributes()
  if (sharedAttributes) {
    Cookies.remove(key, sharedAttributes)
  }

  // Re-apply the host-only value if removing a duplicate exposed a failed write.
  if (Cookies.get(key) !== value) {
    Cookies.set(key, value, hostAttributes)
  }
}

export function setSharedCookie(
  key: string,
  value: string,
  options: CookieLifetimeOptions = {},
): void {
  const sharedAttributes = buildSharedCookieAttributes(options)
  if (!sharedAttributes) {
    setHostCookie(key, value, options)
    return
  }

  Cookies.set(key, value, sharedAttributes)
  Cookies.remove(key, buildHostCookieAttributes())

  if (Cookies.get(key) !== value) {
    Cookies.set(key, value, sharedAttributes)
  }

  // Preserve a usable local session if the browser rejects the shared scope.
  if (Cookies.get(key) !== value) {
    Cookies.set(key, value, buildHostCookieAttributes(options))
  }
}

export function removeCookieFromAllScopes(key: string): void {
  Cookies.remove(key, buildHostCookieAttributes())

  const sharedAttributes = buildSharedCookieAttributes()
  if (sharedAttributes) {
    Cookies.remove(key, sharedAttributes)
  }
}
